/**
 * API Service pour les Choix de Rattrapage (session 2).
 */

import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithHeaders } from './baseApi';

// ─── Types — Analyse d'éligibilité ───────────────────────────────────────────

export interface MatiereRattrapage {
  matiere_id: number;
  code_matiere: string;
  nom_matiere: string;
  credits: number;
  note_devoir: number | null;
  note_examen: number | null;
  absent_examen: boolean;
  moyenne_ecue: number | null;
  eliminatoire_ecue: boolean;
  rattrapable: boolean;
  deja_choisi: boolean;
}

export interface UERattrapage {
  ue_id: number;
  code_ue: string;
  nom_ue: string;
  moyenne_ue: number | null;
  validee: boolean;
  eliminatoire: boolean;
  obligatoire_a_rattraper: boolean;
  recommande_rattraper: boolean;
  matieres: MatiereRattrapage[];
}

export interface AnalyseEligibiliteEtudiant {
  etudiant_id: number;
  matricule: string;
  nom: string;
  prenoms: string;
  classe: string;
  semestre: number;
  annee_academique: string;
  moyenne_semestre: number | null;
  semestre_valide: boolean;
  a_ue_eliminatoire: boolean;
  concerne_par_rattrapage: boolean;
  seuils: {
    seuil_eliminatoire_ue: number;
    seuil_validation_ue: number;
    seuil_validation_semestre: number;
    seuil_eliminatoire_ecue: number;
  };
  ues: UERattrapage[];
}

export interface AnalyseEligibiliteClasse {
  classe_code: string;
  semestre: number;
  annee_academique: string;
  total_classe: number;
  total_concernes: number;
  etudiants: AnalyseEligibiliteEtudiant[];
}

// ─── Types — Simulation ──────────────────────────────────────────────────────

export interface SimulationECUERattrapage {
  matiere_id: number;
  code_matiere: string;
  simulee: boolean;
  note_examen_avant: number | null;
  note_examen_apres: number;
  moyenne_ecue_apres: number;
}

export interface SimulationUERattrapage {
  ue_id: number;
  code_ue: string;
  moyenne_avant: number | null;
  validee_avant: boolean;
  eliminatoire_avant: boolean;
  moyenne_apres: number | null;
  validee_apres: boolean | null;
  eliminatoire_apres: boolean | null;
  sortie_eliminatoire?: boolean;
  simulation_complete: boolean;
  ecues: SimulationECUERattrapage[];
}

export interface SimulationRattrapageResponse {
  etudiant_id: number;
  note_examen_simulee: number;
  matieres_choisies: number[];
  ues: SimulationUERattrapage[];
  moyenne_semestre_avant: number | null;
  moyenne_semestre_apres: number | null;
  semestre_valide_avant: boolean;
  semestre_valide_apres: boolean | null;
  choix_efficaces: boolean;
  nb_ue_restees_eliminatoires: number;
  ue_restees_eliminatoires: string[];
}

// ─── Types — Choix enregistrés ───────────────────────────────────────────────

export interface ChoixMatiereEnregistree {
  choix_id: number;
  matiere_id: number;
  code_matiere: string;
  nom_matiere: string;
  ue_code: string | null;
  note_examen_session1: number | null;
  moyenne_ecue_session1: number | null;
  moyenne_ue_session1: number | null;
  statut: 'selectionne' | 'note_saisie' | 'annule';
  date_selection: string;
  selectionne_par: string | null;
}

export interface ChoixEtudiantEnregistre {
  etudiant_id: number;
  matricule: string;
  nom: string;
  prenoms: string;
  matieres: ChoixMatiereEnregistree[];
}

export interface ChoixClasseResponse {
  classe_code: string;
  semestre: number;
  annee_academique: string;
  nb_etudiants: number;
  nb_choix_total: number;
  etudiants: ChoixEtudiantEnregistre[];
}

// ─── Params ──────────────────────────────────────────────────────────────────

export interface AnalyserEtudiantParams {
  etudiant_id: number;
  semestre: number;
  annee_academique: string;
}

export interface AnalyserClasseParams {
  classe_code: string;
  semestre: number;
  annee_academique: string;
  concernes_seulement?: boolean;
}

export interface SimulerParams {
  etudiant_id: number;
  semestre: number;
  annee_academique: string;
  matieres_ids: number[];
  note_simulee?: number;
}

export interface EnregistrerChoixParams {
  etudiant_id: number;
  semestre: number;
  annee_academique: string;
  matieres_ids: number[];
  commentaire?: string;
}

// ─── API ─────────────────────────────────────────────────────────────────────

export const rattrapageApi = createApi({
  reducerPath: 'rattrapageApi',
  baseQuery: baseQueryWithHeaders,
  tagTypes: ['ChoixRattrapage'],
  endpoints: (builder) => ({
    analyserEligibiliteEtudiant: builder.query<AnalyseEligibiliteEtudiant, AnalyserEtudiantParams>({
      query: ({ etudiant_id, semestre, annee_academique }) =>
        `/choix-rattrapages/analyser-eligibilite/?etudiant_id=${etudiant_id}&semestre=${semestre}&annee_academique=${encodeURIComponent(annee_academique)}`,
      providesTags: ['ChoixRattrapage'],
    }),

    analyserEligibiliteClasse: builder.query<AnalyseEligibiliteClasse, AnalyserClasseParams>({
      query: ({ classe_code, semestre, annee_academique, concernes_seulement = true }) => {
        const qs = new URLSearchParams({
          classe_code, semestre: String(semestre), annee_academique,
          concernes_seulement: String(concernes_seulement),
        });
        return `/choix-rattrapages/par-classe/?${qs.toString()}`;
      },
      providesTags: ['ChoixRattrapage'],
    }),

    simulerRattrapage: builder.mutation<SimulationRattrapageResponse, SimulerParams>({
      query: (body) => ({
        url: '/choix-rattrapages/simuler/',
        method: 'POST',
        body,
      }),
    }),

    enregistrerChoix: builder.mutation<
      { success: boolean; nb_crees: number; nb_reactives: number; nb_annules: number; nb_ignores: number },
      EnregistrerChoixParams
    >({
      query: (body) => ({
        url: '/choix-rattrapages/enregistrer-choix/',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['ChoixRattrapage'],
    }),

    getChoixClasse: builder.query<ChoixClasseResponse, { classe_code: string; semestre: number; annee_academique: string }>({
      query: ({ classe_code, semestre, annee_academique }) =>
        `/choix-rattrapages/choix-classe/?classe_code=${encodeURIComponent(classe_code)}&semestre=${semestre}&annee_academique=${encodeURIComponent(annee_academique)}`,
      providesTags: ['ChoixRattrapage'],
    }),

    annulerChoix: builder.mutation<{ success: boolean; id: number }, number>({
      query: (id) => ({
        url: `/choix-rattrapages/${id}/`,
        method: 'DELETE',
      }),
      invalidatesTags: ['ChoixRattrapage'],
    }),
  }),
});

export const {
  useAnalyserEligibiliteEtudiantQuery,
  useLazyAnalyserEligibiliteEtudiantQuery,
  useAnalyserEligibiliteClasseQuery,
  useLazyAnalyserEligibiliteClasseQuery,
  useSimulerRattrapageMutation,
  useEnregistrerChoixMutation,
  useGetChoixClasseQuery,
  useLazyGetChoixClasseQuery,
  useAnnulerChoixMutation,
} = rattrapageApi;
